import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react'
import { useIonRouter, UseIonRouterResult } from '@ionic/react'
import { Content, DefaultPage } from '@shared/layout/DefaultPage'
import { NavigationArrow } from '@ui/NavigationArrow'

type InfoRow = {
	label: string
	content: ReactNode
}

type Partner = {
	name: string
	rows: InfoRow[]
}

const pageBackground = '#FFFFFF'
const receiptBackground = '#F3F3F0'

const companyRows: InfoRow[] = [
	{
		label: 'Полное наименование:',
		content: 'Общество с ограниченной ответственностью “Тисит”',
	},
	{ label: 'ИНН:', content: '2370015904' },
	{ label: 'ОГРН:', content: '1242300059451' },
	{ label: 'КПП:', content: '237001001' },
	{
		label: 'Юридический адрес:',
		content:
			'353564, Краснодарский край, Славянский район, город Славянск-на-Кубани, улица Школьная, дом 179',
	},
]

const partners: Partner[] = [
	{
		name: 'Спортклуб TITAN',
		rows: [
			{
				label: 'Адрес:',
				content:
					'Краснодарский край, г. Славянск-на-Кубани, ул. Отдельская, д. 310',
			},
			{
				label: 'Инстаграм:',
				content: (
					<ExternalLink href='https://www.instagram.com/titan_sport_snk'>
						titan_sport_snk
					</ExternalLink>
				),
			},
		],
	},
	{
		name: 'Сэм Смбатян',
		rows: [
			{
				label: 'Инстаграм:',
				content: (
					<ExternalLink href='https://www.instagram.com/sam_smbatyan'>
						sam_smbatyan
					</ExternalLink>
				),
			},
			{
				label: 'Телеграм-канал:',
				content: <ExternalLink href='https://t.me/mvr_guys'>@mvr_guys</ExternalLink>,
			},
			{
				label: 'ТикТок:',
				content: <ExternalLink href='https://www.tiktok.com/@slynnel'>slynnel</ExternalLink>,
			},
		],
	},
]

const sources: ReactNode[] = [
	<ExternalLink href='https://www.flaticon.com/'>Flaticon</ExternalLink>,
	<ExternalLink href='https://ru.freepik.com/'>Freepik</ExternalLink>,
	<ExternalLink href='https://www.shutterstock.com/ru/'>Shutterstock</ExternalLink>,
	<ExternalLink href='https://stock.adobe.com/ru/'>Adobe Stock</ExternalLink>,
	<>
		<strong>Figma</strong>: Iconify, Iconduck
	</>,
]

const contentStyle = { '--background': pageBackground } as CSSProperties
const receiptStyle = {
	background: `linear-gradient(180deg, ${receiptBackground} 0%, #EFEFEB 100%)`,
} as CSSProperties

const getReceiptCutoutStyle = (side: 'top' | 'bottom'): CSSProperties => ({
	backgroundImage: `radial-gradient(circle at 10px ${
		side === 'top' ? '0' : '16px'
	}, ${pageBackground} 0 7px, transparent 7.5px)`,
	backgroundRepeat: 'repeat-x',
	backgroundSize: '22px 16px',
})

function ExternalLink({ href, children }: { href: string; children: ReactNode }) {
	return (
		<a
			href={href}
			target='_blank'
			rel='noreferrer'
			style={{ color: '#174ee8', transitionTimingFunction: 'cubic-bezier(0.23,1,0.32,1)' }}
			className='font-semibold underline decoration-[#174ee8]/25 underline-offset-[3px] transition-colors duration-200 active:opacity-70'>
			{children}
		</a>
	)
}

const CompanyDetail = ({ row }: { row: InfoRow }) => (
	<div className='min-w-0 py-3.5'>
		<dt className='text-[15px] font-extrabold leading-[1.25] text-[#111315]'>
			{row.label}
		</dt>
		<dd className='mt-1 break-words text-[17px] leading-[1.45] text-[#202225] [overflow-wrap:anywhere]'>
			{row.content}
		</dd>
	</div>
)

const PartnerItem = ({ partner }: { partner: Partner }) => (
	<article className='min-w-0'>
		<h3 className='text-[22px] font-extrabold leading-[1.12] text-[#111315]'>{partner.name}</h3>
		<div className='mt-3 space-y-2 text-[16px] leading-[1.45] text-[#202225]'>
			{partner.rows.map(row => (
				<p
					key={`${partner.name}-${row.label}`}
					className='min-w-0 break-words [overflow-wrap:anywhere]'>
					<span className='font-semibold text-[#111315]'>{row.label} </span>
					{row.content}
				</p>
			))}
		</div>
	</article>
)

const ReceiptCutout = ({ side }: { side: 'top' | 'bottom' }) => (
	<div
		aria-hidden='true'
		className={`pointer-events-none absolute left-4 right-4 z-10 h-4 ${
			side === 'top' ? 'top-0' : 'bottom-0'
		}`}
		style={getReceiptCutoutStyle(side)}
	/>
)

export const AboutUsPage = () => {
	const nav: UseIonRouterResult = useIonRouter()
	const signatureRef = useRef<HTMLDivElement | null>(null)
	const [isSignatureVisible, setIsSignatureVisible] = useState(false)

	const goBackFunction = () => {
		nav.goBack()
	}

	useEffect(() => {
		const target = signatureRef.current
		if (!target) {
			return
		}

		const observer = new IntersectionObserver(
			entries => {
				if (entries.some(entry => entry.isIntersecting)) {
					setIsSignatureVisible(true)
					observer.disconnect()
				}
			},
			{ threshold: 0.35 }
		)

		observer.observe(target)

		return () => observer.disconnect()
	}, [])

	return (
		<DefaultPage noPadding style={{ background: pageBackground }}>
			<Content style={contentStyle}>
				<div className='mx-auto flex min-h-full w-full max-w-[520px] flex-col px-5 pb-[calc(var(--app-safe-area-bottom)+44px)] pt-[calc(var(--app-safe-area-top)+18px)]'>
					<header className='grid grid-cols-[48px_1fr_48px] items-center'>
						<NavigationArrow
							direction='left'
							onClick={goBackFunction}
							style={{
								width: 48,
								height: 48,
								borderRadius: 16,
								background: '#F7F7F6',
								border: '1px solid rgba(17,19,21,0.055)',
								transition:
									'transform 160ms cubic-bezier(0.23,1,0.32,1), background-color 160ms cubic-bezier(0.23,1,0.32,1)',
							}}
						/>
						<h1 className='text-center text-[18px] font-extrabold leading-none text-[#050505]'>
							О компании
						</h1>
						<div aria-hidden='true' />
					</header>

					<section className='mt-11 text-[#111315]'>
						<div className='space-y-4 text-[20px] leading-[1.42] text-[#111315]'>
							<p className='text-pretty'>
								<strong className='font-extrabold'>ООО “Тисит”</strong> — российская
								компания, зарегистрированная 8 ноября 2024 года.
							</p>
							<p className='text-pretty'>
								Основной вид деятельности — разработка компьютерного программного
								обеспечения.
							</p>
						</div>

						<dl className='mt-7 divide-y divide-black/[0.075] border-y border-black/[0.075]'>
							{companyRows.map(row => (
								<CompanyDetail key={row.label} row={row} />
							))}
						</dl>
					</section>

					<section className='relative mt-12 rounded-[30px] px-5 pb-7 pt-8' style={receiptStyle}>
						<ReceiptCutout side='top' />
						<ReceiptCutout side='bottom' />
						<div className='pointer-events-none absolute inset-x-8 top-6 border-t border-dashed border-black/[0.1]' />
						<div className='relative z-20'>
							<h2 className='text-[31px] font-extrabold leading-[1.02] tracking-[-0.01em] text-[#050505]'>
								Наши партнёры
							</h2>
							<div className='mt-5 space-y-6'>
								{partners.map((partner, index) => (
									<div
										key={partner.name}
										className={index > 0 ? 'border-t border-dashed border-black/[0.12] pt-6' : ''}>
										<PartnerItem partner={partner} />
									</div>
								))}
							</div>

							<div className='mt-7 border-t border-dashed border-black/[0.12] pt-5'>
								<h2 className='max-w-[320px] text-[24px] font-extrabold leading-[1.12] text-[#050505]'>
									Используемые источники
								</h2>
								<ul className='mt-3 space-y-1.5 text-[15px] leading-[1.4] text-[#202225]'>
									{sources.map((source, index) => (
										<li key={index} className='min-w-0 break-words [overflow-wrap:anywhere]'>
											{source}
										</li>
									))}
								</ul>
							</div>

							<div
								ref={signatureRef}
								role='status'
								aria-live='polite'
								className={`mt-7 border-t border-dashed border-black/[0.12] pt-5 text-center transition-[opacity,transform] duration-300 ${
									isSignatureVisible ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
								}`}
								style={{ transitionTimingFunction: 'cubic-bezier(0.23,1,0.32,1)' }}>
								<div className='text-[13px] font-semibold uppercase tracking-[0.18em] text-[#7B7C78]'>
									с любовью
								</div>
								<div className='mt-1 text-[20px] font-extrabold leading-none text-[#111315]'>
									команда TISIT
								</div>
							</div>
						</div>
					</section>
				</div>
			</Content>
		</DefaultPage>
	)
}
